'use client';

import { type ChangeEvent, type InputHTMLAttributes } from 'react';

import { Button, type IconName } from '@/components/ui';
import { cn } from '@/libs';

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  value: string;
  placeholder?: string;
  error?: boolean;
  success?: boolean;
  disabled?: boolean;
  iconLeft?: IconName;
  iconRight?: IconName;
  onIconLeftClick?: () => void;
  onIconRightClick?: () => void;
  onChangeText?: (text: string) => void;
};

export const Input = ({
  value,
  placeholder,
  error,
  success,
  disabled,
  iconLeft,
  iconRight,
  onChangeText,
  onIconLeftClick,
  onIconRightClick,
  className,
  ...props
}: InputProps) => {
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChangeText?.(e.target.value);
  };

  const sharedClassName = cn(
    'flex w-full border-2 caret-foreground bg-background rounded-md border-foreground/25 px-2 h-11 text-base transition-all placeholder:text-muted-foreground ring-offset-background outline-none focus-visible:ring-2  focus-visible:ring-foreground focus-visible:ring-offset-2 disabled:text-foreground/40 disabled:cursor-not-allowed file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground md:text-sm',
    error && 'ring-2 ring-error',
    success && 'ring-2 ring-success',
    iconLeft && 'pl-14',
    iconRight && 'pr-14',
    className,
  );

  return (
    <div className="relative w-full">
      {iconLeft && (
        <Button
          type="button"
          variant="ghost"
          icon={iconLeft}
          onMouseDown={(e) => e.preventDefault()}
          onClick={onIconLeftClick}
          className={cn(
            'absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground sm:hover:bg-foreground/0',
            onIconLeftClick
              ? 'cursor-pointer sm:hover:text-foreground'
              : 'cursor-default',
          )}
        />
      )}

      <input
        value={value ?? ''}
        placeholder={placeholder ?? ''}
        onChange={handleChange}
        disabled={disabled}
        className={sharedClassName}
        {...props}
      />

      {iconRight && (
        <Button
          type="button"
          variant="ghost"
          icon={iconRight}
          onMouseDown={(e) => e.preventDefault()}
          onClick={onIconRightClick}
          className={cn(
            'z-10 absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground sm:hover:bg-foreground/0',
            onIconRightClick
              ? 'cursor-pointer  sm:hover:text-foreground'
              : 'cursor-default',
          )}
        />
      )}
    </div>
  );
};
